import { doc, onSnapshot } from 'firebase/firestore'
import {
  bridgePatientProjectionSchema,
  canvasPatientProjectionSchema,
  tellescopeEnduserProjectionSchema,
  type CustomerId,
} from '@gh/common/models'
import { firestore } from '@/firebase'

const DEFAULT_CLINICAL_VENDOR_PROJECTION_TIMEOUT_MS = 120_000

export const CLINICAL_VENDOR_PROJECTIONS_WAIT_TIMEOUT_MESSAGE =
  'Clinical vendor setup is still in progress. Please wait a moment and try again.'

type LeanPatientProjectionKey = 'bridgePatient' | 'canvasPatient' | 'tellescopeEnduser'

const projectionParsers = {
  bridgePatient: bridgePatientProjectionSchema,
  canvasPatient: canvasPatientProjectionSchema,
  tellescopeEnduser: tellescopeEnduserProjectionSchema,
} as const

/**
 * Wait until customer lean projections report `patientExists` (L4 vendorNativeId).
 * Ensure callables only write L3.5; package create / insured gates require L4 for
 * Tellescope (and Bridge on insured path). Canvas is background-only for booking.
 */
export async function waitForClinicalVendorPatientsReady(
  customerId: CustomerId,
  options?: {
    timeoutMs?: number
    keys?: LeanPatientProjectionKey[]
    signal?: AbortSignal
  },
): Promise<void> {
  if (options?.signal?.aborted) {
    throw new DOMException('Clinical vendor patient wait cancelled', 'AbortError')
  }
  const timeoutMs = options?.timeoutMs ?? DEFAULT_CLINICAL_VENDOR_PROJECTION_TIMEOUT_MS
  const keys = options?.keys ?? ['bridgePatient', 'tellescopeEnduser']
  const signal = options?.signal

  const cleanups: (() => void)[] = []
  try {
    await Promise.all(
      keys.map(
        (key) =>
          new Promise<void>((resolve, reject) => {
            let settled = false
            const ref = doc(firestore, 'customers', customerId, 'projections', key)
            const parser = projectionParsers[key]

            const finish = (settle: () => void) => {
              if (settled) return
              settled = true
              settle()
            }

            const onAbort = () => {
              finish(() => {
                reject(new DOMException('Clinical vendor patient wait cancelled', 'AbortError'))
              })
            }

            const timeout = setTimeout(() => {
              finish(() => {
                reject(new Error(CLINICAL_VENDOR_PROJECTIONS_WAIT_TIMEOUT_MESSAGE))
              })
            }, timeoutMs)

            const syncUnsubscribe = onSnapshot(
              ref,
              (snapshot) => {
                if (settled) return
                if (!snapshot.exists()) {
                  return
                }
                try {
                  const projection = parser.parse({
                    id: snapshot.id,
                    ...snapshot.data(),
                  })
                  if (projection.patientExists === true) {
                    finish(resolve)
                  }
                } catch (error) {
                  finish(() => {
                    reject(error instanceof Error ? error : new Error(String(error)))
                  })
                }
              },
              (error) => {
                finish(() => {
                  reject(error)
                })
              },
            )

            if (signal) {
              signal.addEventListener('abort', onAbort)
              if (signal.aborted) {
                onAbort()
              }
            }

            cleanups.push(() => {
              clearTimeout(timeout)
              syncUnsubscribe()
              signal?.removeEventListener('abort', onAbort)
            })
          }),
      ),
    )
  } finally {
    cleanups.forEach((cleanup) => cleanup())
  }
}
